import { Injectable } from '@angular/core';
import { HttpClient } from '../shared/wrappers/HttpClient';
import { Company } from '../models/Company.model';
import { CompanyGroup } from "../models/CompanyGroup.model";
import { CompanySearch } from '../models/CompanySearch.model';
import { Search } from '../models/Search.model';
import { CompanyGroupSearch } from '../models/CompanyGroupSearch.model';
import { UrlBuilder } from '../models/utils/url-builder.model';
import { ConsultantService } from './consultant.service';
import { isArray } from 'util';
import { CompanyContract } from '../models/CompanyContract.model';

@Injectable()
export class CompaniesService {
    private endPoint: String = "/companies";

    constructor(private httpClient: HttpClient, private consultantService: ConsultantService) {
    }

    async searchCompanies(companySearch: CompanySearch, project: string = ""): Promise<Company[]> {

        const builder = new UrlBuilder([
            { key: "skip", value: companySearch.skip },
            { key: "limit", value: companySearch.limit }
        ]);

        builder.addQueryStringParamsInsideFilter([
            { key: "name", value: companySearch.name },
            { key: "owner", value: companySearch.owner },
            { key: "cnpj", value: companySearch.cnpj },
            { key: "group", value: companySearch.group },
            { key: "active", value: companySearch.active },
            { key: "onlyExternalReference", value: companySearch.onlyExternalReference },
            { key: "onlyActives", value: companySearch.onlyActives },
            { key: "month", value: companySearch.month },
            { key: "monthend", value: companySearch.monthend },
            { key: "year", value: companySearch.year },
            { key: "yearend", value: companySearch.yearend },
            { key: "onlyCustomer", value: companySearch.onlyCustomer },
            { key: "companyGroup", value: companySearch.companyGroup },
            { key: "industries", value: companySearch.industries },
        ]);

        if (project !== '')
            builder.addQueryStringParamsInsideProject(project);

        let url = builder.buildUrl(`${this.endPoint}`);
        var response = await this.httpClient.get(url);
        companySearch.totalItems = response.totalItems;
        var companyModel = new Array<Company>();

        for (let c of response.companies) {
            var con = new Company();
            con.loadCompanyForGrid(c)
            companyModel.push(con);
        }

        return companyModel;
    }

    async getById(id: string): Promise<Company> {
        var companyToEdit = new Company();
        var response = await this.httpClient.get(`${this.endPoint}/${id}`);
        companyToEdit.loadToEdit(response);
        return companyToEdit;
    }

    async create(company: Company) {
        return await this.httpClient.post(this.endPoint, company);
    }

    async edit(company: Company) {
        return await this.httpClient.update(this.endPoint, company._id, company);
    }

    async listAll(): Promise<Company[]> {

        let companies = new Array<Company>();
        let companiesFromServer = await this.httpClient.get(this.endPoint);

        for (let c of companiesFromServer.companies) {
            var company = new Company();
            company._id = c._id;
            company.name = c.name;

            if (c.externalReference) {
                company.externalReference.loadExternalReference(c.externalReference);
            }

            companies.push(company);
        }

        return companies;
    }

    async listAllWithBillings(): Promise<Company[]> {

        let companiesReturn = new Array<Company>();
        let data = (await this.httpClient.get(`${this.endPoint}/with-billings`));

        let companies = [];

        if (data.companies) {

            companies = data.companies.filter(x => x._id);
            companiesReturn = companies.map(company => {
                return new Company(company);
            });
        }

        return companiesReturn;
    }

    async verifyCnpj(company: Company): Promise<Company[]> {
        let companies = new Array<Company>();
        let data = await this.httpClient.post(`${this.endPoint}/cnpj`, company);
        if (isArray(data)) {
            companies = data.map(company => new Company(company));
        }
        return companies
    }

    async getCnpj(cnpj: String) {
        return await this.consultantService.getCnpj(cnpj);
    }

    cnpjValid(cnpj: String) {
        return this.consultantService.cnpjValid(cnpj);
    }

    async listAllGroupsWithBillings(): Promise<CompanyGroup[]> {

        let groupsReturn = new Array<CompanyGroup>();
        let data = (await this.httpClient.get(`${this.endPoint}/group/with-billings`));
        let groups = [];

        if (data.companyGroups) {
            groups = data.companyGroups.filter(x => x._id);
            groupsReturn = groups.map(group => {
                let companyGroup = new CompanyGroup();
                companyGroup._id = group._id;
                companyGroup.name = group.name;
                return companyGroup;
            });
        }

        return groupsReturn;
    }

    async searchCompanyGroups(companyGroupSearch: CompanyGroupSearch): Promise<CompanyGroup[]> {
        let builder = !companyGroupSearch.allItems ? new UrlBuilder([
            { key: "skip", value: companyGroupSearch.skip },
            { key: "limit", value: companyGroupSearch.limit }
        ]) : new UrlBuilder([]);

        builder.addQueryStringParamsInsideFilter([
            { key: "name", value: companyGroupSearch.name },
        ]);

        let url = builder.buildUrl(`${this.endPoint}/group`);
        let response = await this.httpClient.get(url , '', 'companyGroups');
        companyGroupSearch.totalItems = response.totalItems;
        let companyGroupModel = new Array<CompanyGroup>();
        for (let cg of response.groups) {
            var companyGroup = new CompanyGroup();
            companyGroup.loadGroups(cg)
            companyGroupModel.push(companyGroup);
        }
        return companyGroupModel;
    }

    async createGroup(group: CompanyGroup) {
        return await this.httpClient.post(`${this.endPoint}/group`, group);
    }

    async editGroup(group: CompanyGroup, nameEdit) {
        let cg: CompanyGroup = group;
        cg.name = nameEdit;
        return await this.httpClient.update(`${this.endPoint}/group`, cg._id, cg);
    }

    async getCompaniesByGroupId(groupId): Promise<Company[]> {
        const companies = (await this.httpClient.get(`${this.endPoint}/group/${groupId}/companies`)).companies.map(company => {
            return new Company(company);
        });
        return companies;
    }

    async generateContract(data: {company: Company, contract: CompanyContract}) {
        return await this.httpClient.post(`${this.endPoint}/contract`, data);
    }

    async finalizeRegistration(data: any) {
        return await this.httpClient.post(`${this.endPoint}/finalize-registration/${data.company._id}`, data);
    }

    async listVendors() {
        return await this.httpClient.get(`${this.endPoint}/vendors`);
    }

    async searchTemporaryCompanies(companySearch: Search): Promise<any[]> {
        const builder = new UrlBuilder([
            { key: "skip", value: companySearch.skip },
            { key: "limit", value: companySearch.limit }
        ]);

        // builder.addQueryStringParamsInsideFilter([ { key: "name", value: companySearch.name } ])

        let url = builder.buildUrl(`${this.endPoint}/temporary`);
        let response = await this.httpClient.get(url);
        companySearch.totalItems = response.length;

        return response;
    }

    async createTemporaryCompany(company: any) {
        return await this.httpClient.post(`${this.endPoint}/temporary`, company);
    }

    async approvalTemporaryCompany(company: any) {
        return await this.httpClient.post(`${this.endPoint}/temporary/approval`, company);
    }

    async renewTemporaryCompany(id: String) {
        return await this.httpClient.post(`${this.endPoint}/temporary/renew`, { id });
    }


    /* Serviços destinados ao acesso público do Link de autocadastro */
    async getTemporaryCompany(hash: string): Promise<any> {
        let companyToEdit = new Company();
        const response = await this.httpClient.get(`/temporary-company/hash/${hash}`);
        companyToEdit.loadToEdit(response)
        return {
            company: companyToEdit,
            completedFill: response.completedFill,
            expired: response.expired,
            products: response.products && response.products.length > 0 ? response.products : []
        };
    }

    async updateTemporaryCompany(company: Company) {
        return await this.httpClient.update('/temporary-company', company._id, company);
    }

    async getCnpjTemporaryCompany(cnpj: String) {
        let cnpjNumber = cnpj.replace(/\./g, "");
        cnpjNumber = cnpjNumber.replace(/\//g, "");
        cnpjNumber = cnpjNumber.replace(/\-/g, "");
        let response = await this.httpClient.get(`/temporary-company/cnpj/${cnpjNumber}`);
        return response;
    }
}



// WEBPACK FOOTER //
// ./src/app/services/companies.service.ts